<script>
import moment from 'moment';
import FILTER_TYPE from '../common/enum/filter-types';

export default {
  name: 'AuditFilterFields',
  props: {
    id: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    value: {
      type: undefined, // any,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
    hiddenFilters: {
      type: Array,
      default: () => [],
    },
    filterTypes: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    filterID: 0,
    filterType: null,
    filterValue: null,
    filterDateStart: null,
    filterDateEnd: null,
    filterTimeStart: null,
    filterTimeEnd: null,
    filterTags: [],
    filterActorEmail: null,
    filterTargetReference: null,
    selectOptions: {
      [FILTER_TYPE.ACTION_TYPE]: [
        { value: 'authentication', text: 'Authentication' },
        { value: 'creation', text: 'Create' },
        { value: 'deletion', text: 'Delete' },
        { value: 'modification', text: 'Update' },
        { value: 'retrieval', text: 'View' },
      ],
      [FILTER_TYPE.ACTOR_TYPE]: [
        { value: 'acl_user', text: 'ACL_USER' },
        { value: 'lc_user', text: 'LC_USER' },
        { value: 'member', text: 'MEMBER' },
        { value: 'provider', text: 'PROVIDER' },
        { value: 'system', text: 'SYSTEM' },
      ],
      [FILTER_TYPE.TARGET_TYPE]: [
        { value: 'acl_user', text: 'ACL_USER' },
        { value: 'client', text: 'CLIENT' },
        { value: 'lc_user', text: 'LC_USER' },
        { value: 'member', text: 'MEMBER' },
        { value: 'program', text: 'PROGRAM' },
        { value: 'production_work_order', text: 'PRODUCTION_WORK_ORDER' },
        { value: 'system', text: 'SYSTEM' },
        { value: 'device', text: 'DEVICE' },
        { value: 'tracker', text: 'TRACKER' },
        { value: 'integration', text: 'INTEGRATION' },
        { value: 'release', text: 'RELEASE' },
        { value: 'export', text: 'EXPORT' },
        { value: 'box', text: 'BOX' },
        { value: 'sku', text: 'SKU' },
        { value: 'hardware_version', text: 'HARDWARE_VERSION' },
        { value: 'operator', text: 'OPERATOR' },
        { value: 'pod', text: 'POD' },
        { value: 'semi_kit', text: 'SEMI_KIT' },
        { value: 'entity_tag', text: 'ENTITY_TAG' },
        { value: 'm_band', text: 'M_BAND' },
        { value: 'production_tools_setting', text: 'PRODUCTION_TOOLS_SETTING' },
        { value: 'task', text: 'TASK' },
        { value: 'configuration', text: 'CONFIGURATION' },
      ],
      [FILTER_TYPE.IS_BULK]: [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
      ],
    },
    datepickerFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
  }),
  computed: {
    availableFilterTypes() {
      return this.filterTypes.filter((t) => (
        !this.hiddenFilters.find((h) => h === t.value)
      ));
    },
    valueInputType() {
      switch (this.filterType) {
        case FILTER_TYPE.ACTOR_ID:
        case FILTER_TYPE.APPLICATION_NAME:
        case FILTER_TYPE.CLIENT_ID:
        case FILTER_TYPE.TARGET_ID:
        case FILTER_TYPE.TRANSACTION_ID:
        case FILTER_TYPE.TAGS:
        case FILTER_TYPE.ACTOR_EMAIL:
        case FILTER_TYPE.TARGET_REFERENCE:
        case FILTER_TYPE.ENTITY_TYPE:
        case FILTER_TYPE.ENTITY_ID:
          return 'text-input';
        case FILTER_TYPE.ACTION_TYPE:
        case FILTER_TYPE.ACTOR_TYPE:
        case FILTER_TYPE.TARGET_TYPE:
        case FILTER_TYPE.IS_BULK:
          return 'select';
        case FILTER_TYPE.CREATION_PERIOD:
        case FILTER_TYPE.COMPLETION_PERIOD:
          return 'date-range';
        default:
          return 'disabled_input';
      }
    },
    textInputPlaceholder() {
      return (this.filterType === FILTER_TYPE.TAGS)
        ? 'Type tags separated by comma. E.g.: tag1, tag2, tag3'
        : 'Type a value for this filter';
    },
    minEndDate() {
      return moment(this.filterDateStart).toDate();
    },
  },
  mounted() {
    this.filterID = this.id ?? null;
    this.filterType = this.type ?? null;

    if (!this.value) {
      this.resetValue();
    } else if (this.valueInputType === 'date-range') {
      const [startDate, startTime] = this.value[0].split(' ');
      const [endDate, endTime] = this.value[1].split(' ');

      this.filterDateStart = startDate;
      this.filterTimeStart = startTime;
      this.filterDateEnd = endDate;
      this.filterTimeEnd = endTime;
    } else {
      this.filterValue = this.value;
    }

    this.updateFilter();
  },
  methods: {
    resetValue() {
      this.filterValue = null;
      this.filterDateStart = moment().format('YYYY-MM-DD');
      this.filterDateEnd = moment().format('YYYY-MM-DD');
      this.filterTimeStart = '00:00:00';
      this.filterTimeEnd = '23:59:59';
      this.filterTags.splice(0, this.filterTags.length);
    },
    addTag(newTag) {
      this.filterTags.push(newTag);
    },
    filterTypeChanged() {
      this.resetValue();
      this.updateFilter();
    },
    getDateValues() {
      const startDatetime = `${this.filterDateStart ?? moment().format('YYYY-MM-DD')} ${this.filterTimeStart ?? '00:00:00'}`;
      const endDatetime = `${this.filterDateEnd ?? moment().format('YYYY-MM-DD')} ${this.filterTimeEnd ?? '23:59:59'}`;

      return [startDatetime, endDatetime];
    },
    addNewFilter() {
      if (this.filterType === null) {
        this.$noty.error('Filter type must not be empty');
        return;
      }

      if (this.valueInputType === 'select' && this.filterValue === null) {
        this.$noty.error('Select one of the value options');
        return;
      }

      if (this.valueInputType === 'date-range') {
        this.filterValue = this.getDateValues();
      }

      this.$emit('added-filter', {
        type: this.filterType,
        value: this.filterValue,
      });

      this.filterType = null;
      this.resetValue();
    },
    updateFilter() {
      const value = (this.filterID && this.valueInputType === 'date-range')
        ? this.getDateValues()
        : this.filterValue;

      this.$emit('updated-filter', {
        id: this.filterID,
        type: this.filterType,
        value,
      });
    },
    removeFilter() {
      this.$emit('removed-filter', {
        id: this.filterID,
        type: this.filterType,
        value: this.filterValue,
      });
    },
    startDatetimeChange() {
      const endsBeforeStart = moment(`${this.filterDateEnd} ${this.filterTimeEnd}`)
        .isBefore(`${this.filterDateStart} ${this.filterTimeStart}`);

      if (endsBeforeStart) {
        this.filterDateEnd = this.filterDateStart;
        this.filterTimeEnd = this.filterTimeStart;
      }

      this.updateFilter();
    },
  },
};
</script>

<template>
  <div class="d-flex align-items-center w-100">
    <div
      v-if="!required"
      class="mr-2 w-25"
    >
      <b-form-select
        id="new-field"
        v-model="filterType"
        :options="availableFilterTypes"
        @change="filterTypeChanged"
      >
        <template #first>
          <b-form-select-option
            :value="null"
            disabled
          >
            {{ availableFilterTypes.length ? 'Select a filter type' : 'No more filters available' }}
          </b-form-select-option>
        </template>
      </b-form-select>
    </div>

    <!-- text-input -->
    <div
      v-if="valueInputType === 'text-input'"
      class="mr-2 flex-fill"
    >
      <b-form-input
        v-model="filterValue"
        class="w-100"
        :placeholder="textInputPlaceholder"
        @change="updateFilter"
        @keyup.enter="addNewFilter"
      />
    </div>
    <!-- /text-input -->

    <!-- select -->
    <div
      v-else-if="valueInputType === 'select'"
      class="mr-2 flex-fill"
    >
      <b-form-select
        v-model="filterValue"
        :options="selectOptions[filterType]"
        @input="updateFilter"
      >
        <template #first>
          <b-form-select-option
            :value="null"
            disabled
          >
            Select an option
          </b-form-select-option>
        </template>
      </b-form-select>
    </div>
    <!-- /select -->

    <!-- date-range -->
    <div
      v-else-if="valueInputType === 'date-range'"
      class="mr-2 flex-fill"
    >
      <div class="d-flex justify-content-between">
        <div class="flex-fill mr-2">
          <b-input-group>
            <b-form-datepicker
              v-model="filterDateStart"
              placeholder="Start date"
              left
              :date-format-options="datepickerFormatOptions"
              @input="startDatetimeChange"
            />
            <b-form-timepicker
              v-model="filterTimeStart"
              placeholder="Start time"
              :hour12="false"
              show-seconds
              hide-header
              @input="startDatetimeChange"
            />
          </b-input-group>
        </div>

        <div class="flex-fill">
          <b-input-group>
            <b-form-datepicker
              v-model="filterDateEnd"
              placeholder="End date"
              :date-format-options="datepickerFormatOptions"
              :min="minEndDate"
              left
              @input="updateFilter"
            />
            <b-form-timepicker
              v-model="filterTimeEnd"
              placeholder="End time"
              :hour12="false"
              show-seconds
              hide-header
              @input="updateFilter"
            />
          </b-input-group>
        </div>
      </div>
    </div>
    <!-- /date-range -->

    <!-- disabled_input -->
    <div
      v-else
      class="mr-2 flex-fill"
    >
      <b-form-input
        style="cursor: not-allowed;"
        placeholder="Select a filter type"
        disabled
      />
    </div>
    <!-- /disabled_input -->

    <div
      v-if="id && !required"
      style="width: 130px;"
    >
      <b-button
        variant="danger"
        class="w-100"
        @click="removeFilter"
      >
        Remove
        <feather type="trash-2" />
      </b-button>
    </div>

    <div
      v-else-if="!required"
      style="width: 130px;"
    >
      <b-button
        variant="primary"
        class="w-100"
        @click.prevent="addNewFilter"
      >
        Add filter
        <feather type="plus-circle" />
      </b-button>
    </div>
  </div>
</template>
